import React, { useRef } from 'react'
import type { Identifier, XYCoord } from "dnd-core";
import { useDrag, useDrop } from 'react-dnd'
import { ICustomRatingCategory } from '../../app/interfaces/ICustomRatingCategory';
import styled from 'styled-components';
import { CustomRatingCategory } from './CustomRatingCategory';

interface IDraggableCategoryProps {
  category: ICustomRatingCategory;
  id: number;
  index: number;
  move: (dragIndex: number, hoverIndex: number) => void;
}

const draggableType = 'CUSTOM_RATING_CATEGORY';

/**
 * Borrowed liberally from
 * https://react-dnd.github.io/react-dnd/examples/sortable/simple
 */
const DraggableCategory = ({category, index, id, move,}: IDraggableCategoryProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{handlerId}, drop] = useDrop<
    IDraggableCategoryProps,
    void,
    { handlerId: Identifier | null }
  >({
    accept: draggableType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item: IDraggableCategoryProps, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      move(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      
      item.index = hoverIndex;
    }
  })


  const [{ isDragging }, drag] = useDrag({
    type: draggableType,
    item: () => {
      return { id, index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  drag(drop(ref));

  return (
    <StyledPaddedDraggable ref={ref} data-handler-id={handlerId} $isDragging={isDragging}>
      <CustomRatingCategory {...category} />
    </StyledPaddedDraggable>
  );
};

export default DraggableCategory;


const StyledPaddedDraggable = styled('div')<{ $isDragging: boolean }>`
  cursor: move;
  margin-bottom: 10px;
  opacity: ${props => props.$isDragging ? 0 : 1}
`;